/**
 * F17 T17.1.5 — ParallelJoinNode: zbieżność gałęzi równoległych.
 *
 * Czeka aż wszystkie inbound edges (gałęzie z ParallelSplit) dotrą. Atomic
 * UPDATE w engine.ts gwarantuje race-safety (parallel_paths_completed array).
 *
 * Handle layout: 2 target handles top z różnymi `id`, source bottom.
 * Commit 3 freeze: 2 hard-coded ścieżki — patrz komentarz w ParallelSplitNode.tsx.
 *
 * `data.isCurrent` (F17 T17.1.8c) — podświetlenie w read-only `ProcessGraphViewer`. Builder
 * nigdy tego pola nie ustawia (patrz `graphTypes.ts`), więc render w builderze bez zmian.
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { GitMerge } from 'lucide-react';

import { CURRENT_NODE_RING_CLASS } from '@/lib/processes/step-tone';

interface ParallelJoinNodeData {
  isCurrent?: boolean;
  [key: string]: unknown;
}

export const ParallelJoinNode = memo(function ParallelJoinNode({ data, selected }: NodeProps) {
  const { t } = useTranslation();
  const isCurrent = (data as ParallelJoinNodeData)?.isCurrent ?? false;
  return (
    <div
      className={`flex h-[50px] w-[100px] items-center justify-center rounded-lg bg-gray-600 shadow-lg transition-shadow dark:bg-gray-500 ${
        selected ? 'ring-2 ring-accent-500 ring-offset-2 dark:ring-offset-gray-900' : ''
      } ${isCurrent ? CURRENT_NODE_RING_CLASS : ''}`}
      data-testid="process-node-parallel-join"
      data-current={isCurrent ? 'true' : 'false'}
    >
      <GitMerge className="h-5 w-5 text-white" aria-hidden="true" />
      <span className="ml-1 text-xs font-semibold text-white">
        {t('tenant.processes.builder.nodes.parallelJoin')}
      </span>
      {isCurrent && <span className="sr-only">{t('tenant.processes.flow.currentStepAria')}</span>}
      <Handle
        type="target"
        position={Position.Top}
        id="path_1"
        className="!left-[30%] !h-3 !w-3 !border-2 !border-white !bg-gray-500"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="path_2"
        className="!left-[70%] !h-3 !w-3 !border-2 !border-white !bg-gray-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-3 !w-3 !border-2 !border-white !bg-gray-500"
      />
    </div>
  );
});
